import { useQuery } from "@tanstack/react-query";
import { api } from "../../services/api";
import type {
  Tote,
  Trolley,
  AssetTrackingData,
  ToteStatusListResponse,
  TrolleyStatusListResponse,
  LocationListResponse,
  TotePriorityOption,
  PrimaryLocationItem,
} from "../../services/types";

// Query keys
export const queryKeys = {
  tote: (id: string) => ["tote", id] as const,
  totes: () => ["totes"] as const,
  trolley: (id: string) => ["trolley", id] as const,
  trolleys: () => ["trolleys"] as const,
  assetTracking: (filter?: string) => ["assetTracking", filter] as const,
  toteStatus: () => ["toteStatus"] as const,
  trolleyStatus: () => ["trolleyStatus"] as const,
  locations: () => ["locations"] as const,
  totePriorityList: () => ["totePriorityList"] as const,
  primaryLocations: () => ["primaryLocations"] as const,
};

// Queries
export const useToteById = (id: string | undefined) => {
  return useQuery<Tote, Error>({
    queryKey: queryKeys.tote(id || ""),
    queryFn: () => api.getToteById(id!),
    enabled: !!id,
    staleTime: 0,
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
};

export const useAllTotes = (
  params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status_ids?: number[];
    priority_ids?: number[];
    location_id?: number;
    location_barcode?: string;
    from_date?: string;
    to_date?: string;
  },
  enabled: boolean = true
) => {
  // Create a stable query key by serializing params
  const queryKey = [
    ...queryKeys.totes(),
    params?.page ?? 1,
    params?.page_size ?? 100,
    params?.search || '',
    params?.status_ids?.join(',') || '',
    params?.priority_ids?.join(',') || '',
    params?.location_id || '',
    params?.location_barcode || '',
    params?.from_date || '',
    params?.to_date || '',
  ];

  return useQuery<Tote[], Error>({
    queryKey,
    queryFn: () => api.getAllTotes(params),
    enabled: enabled && !!params, // Only run when enabled AND params exist
    staleTime: 0,
    refetchOnMount: false, // Disable automatic refetch on mount
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useTrolleyById = (id: string | undefined) => {
  return useQuery<Trolley, Error>({
    queryKey: queryKeys.trolley(id || ""),
    queryFn: () => api.getTrolleyById(id!),
    enabled: !!id,
    staleTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
};

export const useAllTrolleys = (
  params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status_ids?: number[];
    priority_flag?: boolean | boolean[];
    location_id?: number;
    location_barcode?: string;
    from_date?: string;
    to_date?: string;
  },
  enabled: boolean = true
) => {
  // Create a stable query key by serializing params
  const queryKey = [
    ...queryKeys.trolleys(),
    params?.page ?? 1,
    params?.page_size ?? 100,
    params?.search || '',
    params?.status_ids?.join(',') || '',
    params?.priority_flag ?? '',
    params?.location_id || '',
    params?.location_barcode || '',
    params?.from_date || '',
    params?.to_date || '',
  ];

  return useQuery<Trolley[], Error>({
    queryKey,
    queryFn: () => api.getAllTrolleys(params),
    enabled: enabled && !!params, // Only run when enabled AND params exist
    staleTime: 0,
    refetchOnMount: false, // Disable automatic refetch on mount
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useAssetTracking = (filter?: string) => {
  return useQuery<AssetTrackingData[], Error>({
    queryKey: queryKeys.assetTracking(filter),
    queryFn: () => api.getAllAssetTracking(filter),
    staleTime: 30000, // Cache for 30 seconds
  });
};

export const useTrolleyStatus = () => {
  return useQuery<TrolleyStatusListResponse, Error>({
    queryKey: queryKeys.trolleyStatus(),
    queryFn: () => api.getTrolleyStatus(),
    staleTime: 300000, // Cache for 5 minutes (status list doesn't change often)
  });
};

// New Tote Management Queries
export const useToteStatus = () => {
  return useQuery<ToteStatusListResponse, Error>({
    queryKey: queryKeys.toteStatus(),
    queryFn: api.getToteStatus,
    staleTime: 300000, // Cache for 5 minutes (status list doesn't change often)
  });
};

export const useLocations = () => {
  return useQuery<LocationListResponse, Error>({
    queryKey: queryKeys.locations(),
    queryFn: api.getLocations,
    staleTime: 300000,
  });
};

export const useTotePriorities = () => {
  return useQuery<TotePriorityOption[], Error>({
    queryKey: queryKeys.totePriorityList(),
    queryFn: api.getTotePriorityList,
    staleTime: 300000,
  });
};

export const usePrimaryLocations = () => {
  return useQuery<PrimaryLocationItem[], Error>({
    queryKey: queryKeys.primaryLocations(),
    queryFn: api.getPrimaryLocations,
    staleTime: 300000,
  });
};
